import React, { useState, useEffect } from "react";
import {
  Card,
  Headline,
  Paragraph,
  Button,
  Loading,
  Flex,
  Bank,
} from "_styleguide";
import { getReviewItem } from "./_helpers";
import { useSettings } from "_contexts/useSettings";
import ConfirmPopup from "./_components/ConfirmPopup/ConfirmPopup";
import { getCheckoutTradelines } from "_graphql/queries";
import { useApolloClient } from "@apollo/client";
import { useLead } from "_contexts/useLead";
import { Tooltip } from "@centrate-io/barn";
import { formatMoney } from "_assets/js/helpers";
import dayjs from "dayjs";
import { isDarkColor } from "_helpers/colors";
import "./Review.scss";

function Review(props) {
  const [showWindow, setShowWindow] = useState(false);
  const [creditStats, setCreditStats] = useState(null);
  const client = useApolloClient();
  const pageSettings = props.settings?.settings;
  const { lead, savedPlan, bank } = useLead();
  const { settings } = useSettings();
  const primaryColor = settings?.primary_color || "#0f766e";
  const primaryFontColor = isDarkColor(primaryColor) ? "#fff" : "#000";

  // Load Tradelines
  const fetchTradelines = async () => {
    const queryData = await client.query({
      variables: {
        id: lead?.id,
      },
      query: getCheckoutTradelines,
    });
    const responseData = queryData?.data?.getApplicationTradelines;
    setCreditStats(responseData);
  };

  useEffect(() => {
    fetchTradelines();
  }, []);

  let dateValue = undefined;
  const plan = lead?.plan;
  if (plan?.start_date) {
    if (plan?.start_date?.length <= 13) {
      dateValue = dayjs(parseInt(plan?.start_date));
    } else {
      dateValue = dayjs(plan?.start_date);
    }
  }

  return !creditStats ? (
    <Loading />
  ) : (
    <React.Fragment>
      {showWindow ? (
        <ConfirmPopup
          pageSettings={pageSettings}
          closeWindow={() => setShowWindow(false)}
          {...props}
        />
      ) : null}
      <Card id="review">
        <div className="apply-form">
          <Headline>{pageSettings.title}</Headline>
          {pageSettings?.subtitle ? (
            <Paragraph
              opacity={0.6}
              align="left"
              size="14px"
              dangerouslySetInnerHTML={{
                __html: pageSettings.subtitle,
              }}
            />
          ) : null}
          <div className="review-body">
            {getReviewItem(
              "planName",
              <>
                {savedPlan ? (
                  <Flex align="center">
                    <span
                      className={`plan-circle`}
                      style={{
                        backgroundColor: savedPlan
                          ? savedPlan.background_color
                          : undefined,
                      }}
                    />
                    {savedPlan.name}
                  </Flex>
                ) : (
                  "No Plan Selected"
                )}
              </>
            )}

            {getReviewItem("creditLinesEnrolled", creditStats?.selected || 0)}
            {getReviewItem(
              "creditors",
              <Tooltip
                title={creditStats?.tradelines
                  ?.filter((tradeline) => tradeline?.selected)
                  .map((tradeline, index) => (
                    <div key={`tradeline-${index}`}>
                      {tradeline?.tradeline?.customerName}
                    </div>
                  ))}
              >
                <div className="overflow-ellipsis">
                  {creditStats?.tradelines
                    ?.filter((tradeline) => tradeline?.selected)
                    .map((tradeline) => tradeline?.tradeline?.customerName)
                    .join(", ")}
                </div>
              </Tooltip>
            )}
            {getReviewItem(
              "debtEnrolledTotal",
              formatMoney(creditStats?.balance)
            )}
            {getReviewItem(
              "startDate",
              dateValue?.utc()?.format("ddd, MMMM Do, YYYY") || "N/A"
            )}
            {getReviewItem(
              "paymentAmount",
              <>{formatMoney(plan?.amount)}/ Monthly</>
            )}
            {getReviewItem("planDuration", `${plan?.months || 0} months`)}
            {bank && bank?.account_type !== "creditcard"
              ? getReviewItem("bankAccount", <Bank bankdata={bank} />)
              : null}
            {bank && bank?.account_type === "creditcard"
              ? getReviewItem("cardAccount", <Bank bankdata={bank} />)
              : null}
          </div>
        </div>
        <Button
          block
          size="large"
          style={{ background: primaryColor, color: primaryFontColor }}
          onClick={() => setShowWindow(true)}
        >
          {pageSettings.button_text}
        </Button>
      </Card>
    </React.Fragment>
  );
}

export default Review;
